import type { HTMLAttributes, ReactNode, TdHTMLAttributes, ThHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { AlertTriangle, Inbox, Loader2 } from 'lucide-react';
import { cn } from '../lib/cn';

export const Card = ({ className, ...p }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('rounded-lg border border-line-subtle bg-surface shadow-sm', className)} {...p} />
);

/** Section label in caps; gold is allowed for eyebrows. */
export const Eyebrow = ({ className, ...p }: HTMLAttributes<HTMLParagraphElement>) => (
  <p className={cn('text-xs font-bold uppercase tracking-[0.12em] text-gold-strong', className)} {...p} />
);

export function PageHeader({
  title,
  description,
  eyebrow,
  actions,
}: {
  title: ReactNode;
  description?: ReactNode;
  eyebrow?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div className="grid gap-1">
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        <h1 className="text-2xl font-bold tracking-tight text-fg-1 sm:text-3xl">{title}</h1>
        {description && <p className="max-w-2xl text-sm text-fg-2">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

const badgeVariants = cva('inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-bold', {
  variants: {
    tone: {
      neutral: 'bg-sunken text-fg-2',
      success: 'bg-success-bg text-success',
      warning: 'bg-warning-bg text-warning',
      danger: 'bg-danger-bg text-danger',
      info: 'bg-info-bg text-info',
      outline: 'border border-line text-fg-2',
    },
  },
  defaultVariants: { tone: 'neutral' },
});

/** Status is never colour alone: the badge always carries text (docs/01-architectuur/11-frontend.md). */
export const Badge = ({ className, tone, ...p }: HTMLAttributes<HTMLSpanElement> & VariantProps<typeof badgeVariants>) => (
  <span className={cn(badgeVariants({ tone }), className)} {...p} />
);

export const Kbd = ({ className, ...p }: HTMLAttributes<HTMLElement>) => (
  <kbd className={cn('rounded-sm border border-line bg-sunken px-1.5 font-mono text-[11px] text-fg-2', className)} {...p} />
);

export const Mono = ({ className, ...p }: HTMLAttributes<HTMLSpanElement>) => (
  <span className={cn('rounded-sm bg-mono px-1 font-mono text-[0.92em]', className)} {...p} />
);

export const Skeleton = ({ className, ...p }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('animate-pulse rounded-md bg-sunken', className)} {...p} />
);

export function Spinner({ label }: { label: string }) {
  return (
    <div role="status" className="flex items-center gap-2 text-sm text-fg-3">
      <Loader2 className="size-4 animate-spin" aria-hidden />
      <span>{label}</span>
    </div>
  );
}

export function EmptyState({ title, description, action }: { title: ReactNode; description?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-line px-6 py-12 text-center">
      <Inbox className="size-6 text-fg-4" aria-hidden />
      <p className="font-bold text-fg-1">{title}</p>
      {description && <p className="max-w-sm text-sm text-fg-2">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function ErrorState({ title, description, action }: { title: ReactNode; description?: ReactNode; action?: ReactNode }) {
  return (
    <div role="alert" className="flex flex-col items-center gap-2 rounded-lg border border-danger/40 bg-danger-bg px-6 py-10 text-center">
      <AlertTriangle className="size-6 text-danger" aria-hidden />
      <p className="font-bold text-danger">{title}</p>
      {description && <p className="max-w-md text-sm text-fg-2">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

// ---- Table
export const Table = ({ className, ...p }: HTMLAttributes<HTMLTableElement>) => (
  <div className="w-full overflow-x-auto rounded-lg border border-line-subtle bg-surface">
    <table className={cn('w-full border-collapse text-sm', className)} {...p} />
  </div>
);
export const THead = (p: HTMLAttributes<HTMLTableSectionElement>) => <thead className="bg-surface-alt text-left" {...p} />;
export const TBody = (p: HTMLAttributes<HTMLTableSectionElement>) => <tbody {...p} />;
export const Tr = ({ className, ...p }: HTMLAttributes<HTMLTableRowElement>) => (
  <tr className={cn('border-b border-line-subtle last:border-0 data-[selected=true]:bg-gold-soft/40', className)} {...p} />
);
export const Th = ({ className, ...p }: ThHTMLAttributes<HTMLTableCellElement>) => (
  <th className={cn('whitespace-nowrap px-3 py-2 text-xs font-bold uppercase tracking-wide text-fg-3', className)} {...p} />
);
export const Td = ({ className, ...p }: TdHTMLAttributes<HTMLTableCellElement>) => (
  <td className={cn('px-3 py-2.5 align-middle text-fg-1', className)} {...p} />
);
